import * as React from 'react'
import { useState, useEffect } from 'react'
import { useIntl } from 'react-intl'
import Bugsnag from '@bugsnag/js'
import { SUBMISSIONSTATUSGROUPS } from '@content/config'
import { getKey, getDeviceId, getSessionId } from '@utils/get'
import { relative_replace } from '@utils/helpers'
import { handleUploads, validateBankDetails } from '@utils/form'
import SubmissionClient from '@app/services/SubmissionClient'
import { locale } from '@app/utils/intl'

// Context
import { SubmissionContext } from '.'

import SubmissionCreationException from './exception/SubmissionCreationException'
import NotValidatedException from './exception/NotValidatedException'

const blockingNotValidatedReasons = ['bad request', 'client error']

const SubmissionContextProvider: React.FC<SubmissionProvider.IProps> = (props) => {
  const intl = useIntl()

  const [ submitting, setSubmitting ] = useState({
    inProgress: false,
    remainingRetries: 3,
    failedState: null,
    slow: false,
  })
  const [ error, setError ] = useState<string | null | Error>(null)
  const [ sessionId, setSessionId ] = useState<string | null>(null)
  const [ deviceId, setDeviceId ] = useState<string | null>(null)
  const [ qualifiedPromoLinks, setQualifiedPromoLinks ] = useState<string | null>(null)

  // test this matches
  const submissionFriendlyStatuses: Submission.Statuses = {
    'Fraud Flag': intl.formatMessage({ defaultMessage: 'Your claim has been received' }),
    Accepted: intl.formatMessage({ defaultMessage: 'Your claim has been approved, and we will be in touch via email or SMS' }),
    Dispatched: intl.formatMessage({ defaultMessage: 'Your prize has been dispatched' }),
    Lost: intl.formatMessage({ defaultMessage: 'Your claim was unsuccessful' }),
    Received: intl.formatMessage({ defaultMessage: 'Your claim has been received' }),
    default: intl.formatMessage({ defaultMessage: 'Unable to get claim status' }),
  }

  useEffect(() => {
    getDeviceId()
      .then((id) => setDeviceId(id))
    getSessionId(deviceId, props.config.submissionUri)
      .then((id) => setSessionId(id))
  }, [])

  const updateSubmitting = (status: any, error?: string | Error) => {
    setSubmitting((prevSubmitting) => ({ ...prevSubmitting, ...status }))
    setError(error)
  }

  const handlePurchaseItemFields = (purchaseItems: any, consumerFields: Record<string, any>) => {
    let totalPrice = 0
    return {
      purchaseItems: purchaseItems.map((purchaseItem: any) => ({
        id: purchaseItem.id,
        fields: Object.keys(purchaseItem.fields).reduce((purchaseItemAcc, field) => {
          const consumerField = consumerFields.find((consumerField: Record<string, any>) => consumerField.field === field)
          const itemFieldValue = purchaseItem.fields[field].value

          switch (field) {
            case 'purchasePrice':
              totalPrice += itemFieldValue.amount
              return {
                ...purchaseItemAcc,
                [field]: itemFieldValue.amount,
              }
            case 'quantity':
              return {
                ...purchaseItemAcc,
                [field]: Number(itemFieldValue),
              }
            case 'productId':
              const execResult = /(?<sku>.*)\|.*/.exec(itemFieldValue)
              if (execResult?.groups.sku) return {
                ...purchaseItemAcc,
                [field]: itemFieldValue,
                sku: execResult.groups.sku,
              }
            default:
              if (consumerField?.regex) {
                const execResult = new RegExp(consumerField.regex, 'gi').exec(itemFieldValue)
                if (execResult) return {
                  ...purchaseItemAcc,
                  [field]: execResult.groups[field],
                }
              }
              return {
                ...purchaseItemAcc,
                [field]: itemFieldValue,
              }
          }
        }, {})
      })),
      deltaTotalPrice: totalPrice,
    }
  }

  const formatPurchaseInformation = (formData: any, uploads: any) => {
    const consumerFields = props.config.campaignConsumerFields
    let totalPrice = 0

    const formattedPurchaseInformation = formData.purchaseInformation.map((item: any) => ({
      id: item.id,
      fields: Object.keys(item.fields).reduce((acc, field) => {
        if (uploads[item.id + '-' + field]) {
          const { filename } = uploads[item.id + '-' + field]
          delete uploads[item.id + '-' + field]
          return { 
            ...acc,
            [field]: filename
          }
        } 

        if (field === 'purchaseItems') {
          const { purchaseItems, deltaTotalPrice } = handlePurchaseItemFields(item.fields[field].value, consumerFields)
          totalPrice += deltaTotalPrice
          return {
            ...acc,
            [field]: purchaseItems,
          }
        }

        return {
          ...acc,
          [field]: item.fields[field].value,
        }
      }, {})
    }))

    if (totalPrice > 0) {
      formData.totalPrice = totalPrice
    }

    return formattedPurchaseInformation
  }

  const submit = async (formData: any) => {
    const { config } = props

    let submissionResponse
    let uploads: any
    let slowTimer

    const remainingRetries = submitting.failedState
      ? submitting.remainingRetries - 1
      : submitting.remainingRetries
    updateSubmitting(
      {
        inProgress: true,
        remainingRetries: remainingRetries,
        failedState: null,
        slow: false,
      },
      null
    )
    slowTimer = setTimeout(() => {
      updateSubmitting({ slow: true }, null)
    }, 3000)

    for (const [field, value] of Object.entries(formData) as any) {
      // only submit normalised amounts for currency fields
      if (value?.amount && value?.currency) {
        formData[field] = value.amount
      }
      // handle bankDetails if present
      if (field === 'bankDetails') {
        // validate bankDetails
        const validationResult = await validateBankDetails(
          config.campaignCountry,
          formData[field],
          `${config.submissionUri}/validateBankDetails`
        )
        // If invalid, stop trying to submit
        if (validationResult.valid === false) {
          clearTimeout(slowTimer)
          updateSubmitting({ failedState: 'validating_bankDetails' }, intl.formatMessage({ defaultMessage: 'invalid bank details' }))
          return
        }

        // Capture reason for not validating in bugsnag
        if (!validationResult.validated) {
          Bugsnag.notify(new NotValidatedException(validationResult.reason))

          if (blockingNotValidatedReasons.includes(validationResult.reason)) {
            clearTimeout(slowTimer)
            updateSubmitting({ failedState: 'validating_bankDetails' }, intl.formatMessage({ defaultMessage: 'error validating bank details' }))
            return
          }
        }
        // add validated flag and strip valid helper prop from submission
        formData[field].validated = validationResult.validated
        delete formData[field].valid
      }
    }

    try {
      uploads = await handleUploads(formData, config.submissionUri, config.campaignId)
    } catch (error) {
      Bugsnag.notify(error)
      clearTimeout(slowTimer)
      updateSubmitting({ failedState: 'uploading_files' }, error)
      return
    }

    if (formData.purchaseInformation) {
      formData.purchaseInformation = formatPurchaseInformation(formData, uploads)
    }

    try {
      submissionResponse = await SubmissionClient.createSubmission({
        sessionId,
        deviceId,
        locale,
        ...formData,
        ...uploads,
      })
      clearTimeout(slowTimer)
      updateSubmitting({ inProgress: false }, null)

      if (submissionResponse) {
        if (config.campaignType === 'winning-moment') {
          const status = submissionResponse?.status

          if (status === 'entry-Received' || status === 'won') {
            window.location.href = relative_replace(
              window.location.pathname,
              `/submission/win/?submissionId=${submissionResponse.submissionId}`
            )
          } else {
            window.location.href = relative_replace(
              window.location.pathname,
              `/submission/lose/?submissionId=${submissionResponse.submissionId}`
            )
          }
        } else {
          window.location.href = relative_replace(
            window.location.pathname,
            `/submission/thank-you/?submissionId=${submissionResponse.submissionId}`
          )
        }
      }
    } catch (error) {
      if (error instanceof Response) {
        if (error.status !== 403) {
          await error.json()
        }
      }

      Bugsnag.notify(new SubmissionCreationException())
      clearTimeout(slowTimer)
      updateSubmitting({ failedState: 'submitting_details' }, error)
    }
  }

  const postAdditionalDetails = async (claimId: string, signature: string, information: any) => {
    const { config } = props

    updateSubmitting({ inProgress: true, failedState: null }, null)

    let slowTimer

    const remainingRetries = submitting.failedState
      ? submitting.remainingRetries - 1
      : submitting.remainingRetries

    updateSubmitting(
      {
        inProgress: true,
        remainingRetries: remainingRetries,
        failedState: null,
        slow: false,
      },
      null
    )
    slowTimer = setTimeout(() => {
      updateSubmitting({ slow: true }, null)
    }, 3000)

    if (information?.bankDetails) {
      const { bankDetails } = information
      const validationResult = await validateBankDetails(
        config.campaignCountry,
        bankDetails,
        `${config.submissionUri}/validateBankDetails`
      )
      // If invalid, stop trying to submit
      if (validationResult.valid === false) {
        clearTimeout(slowTimer)
        updateSubmitting({ failedState: 'validating_bankDetails' }, intl.formatMessage({ defaultMessage: 'invalid bank details' }))
        return
      }

      // Capture reason for not validating in bugsnag
      if (!validationResult.validated) {
        Bugsnag.notify(new NotValidatedException(validationResult.reason))

        if (blockingNotValidatedReasons.includes(validationResult.reason)) {
          clearTimeout(slowTimer)
          updateSubmitting({ failedState: 'validating_bankDetails' }, intl.formatMessage({ defaultMessage: 'error validating bank details' }))
          return
        }
      }
      // add validated flag and strip valid helper prop from submission
      information.bankDetails.validated = validationResult.validated
      delete information.bankDetails.valid
    }

    let uploads: any
    try {
      uploads = await handleUploads(information, config.submissionUri, config.campaignId)
    } catch (error) {
      Bugsnag.notify(error)
      clearTimeout(slowTimer)
      updateSubmitting({ failedState: 'uploading_files' }, error)
      return
    }

    if (information.purchaseInformation) {
      information.purchaseInformation = formatPurchaseInformation(information, uploads)
    } else {
      Object.keys(information).forEach((field) => {
        if (uploads[field]) information[field] = uploads[field]
      })
    }

    try {
      const response = await SubmissionClient.captureInformationRequest(claimId, signature, information)

      if (response) {
        window.location.href = relative_replace(
          window.location.pathname,
          `/claim/information-request/thank-you/?claimId=${claimId}`
        )
      }
    } catch (error) {
      clearTimeout(slowTimer)
      if (error instanceof Error) {
        updateSubmitting({ failedState: 'submitting_additional_details' }, error)
        return
      }

      const body = await error.json()

      const hasLinkExpired = body?.status?.includes('Your link has expired. Please contact us to get a new one.')
      if (hasLinkExpired) {
        updateSubmitting({ failedState: 'submitting_additional_details' }, body?.status)
        return
      }
      updateSubmitting({ failedState: 'submitting_additional_details' }, error)
    }
  }

  const getClaimInformationRequests = async (formData: { claimId: string }) => {
    const { config } = props
    const { claimId } = formData

    const url = `${config.submissionUri}/retrieveClaimInformationRequest`

    try {
      const res = await fetch(url, {
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          campaignId: config.campaignId,
          claimId: claimId,
        }),
        method: 'POST',
      })
      const resBody = await res.json()

      return resBody
    } catch (err) {
      return null
    }
  }

  const getStatus = async (formData: any) => {
    const { config } = props

    updateSubmitting({ inProgress: true, failedState: null }, null)

    const url = `${config.submissionUri}/submission/state/?campaignId=${encodeURIComponent(
      config.campaignId
    )}&submissionId=${encodeURIComponent(formData.submissionId.trim())}`

    try {
      const res = await (
        await fetch(url, {
          headers: { 'Content-Type': 'application/json' },
          method: 'GET',
        })
      ).json()

      const key: Submission.StatusGroup = getKey(SUBMISSIONSTATUSGROUPS, res.status)
      if (res.location && res.location.includes('/submission/additionalDetails')) {
        window.location.href = res.location
        return
      }

      if (key) {
        updateSubmitting({ inProgress: false }, null)
        setQualifiedPromoLinks(res.qualifiedPromoLinks)

        if (!res.qualifiedPromoLinks || res.qualifiedPromoLinks.length == 0) {
          window.location.href = relative_replace(
            window.location.pathname,
            `/submission/status/?submissionStatus=${submissionFriendlyStatuses[key]}`
          )
        }
      } else {
        throw res
      }
    } catch (err) {
      updateSubmitting({ failedState: 'getting_status' }, err)
    }
  }
  const { children, config } = props
  const value = {
    campaignId: config.campaignId,
    submissionUri: config.submissionUri,
    submitting,
    updateSubmitting,
    error,
    submit,
    getStatus,
    postAdditionalDetails,
    getClaimInformationRequests,
    sessionId,
    qualifiedPromoLinks,
  }

  return <SubmissionContext.Provider value={value}>{children}</SubmissionContext.Provider>
}

export default SubmissionContextProvider
